import React, { useState, useEffect } from 'react';
import { Bell, Send, Users, UserCheck, Globe, Loader2, CheckCircle, XCircle, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

function Broadcast() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    targetAudience: 'all',
  });
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await api.getBroadcastStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setSendResult(null);

      const response = await api.sendBroadcastNotification({
        title: formData.title.trim(),
        body: formData.body.trim(),
        targetAudience: formData.targetAudience,
      });

      if (response.success) {
        setSendResult(response.data);
        toast.success(response.message || 'Broadcast notification sent successfully!');
        
        // Reset form
        setFormData({
          title: '',
          body: '',
          targetAudience: 'all',
        });

        // Refresh stats
        fetchStats();
      } else {
        toast.error(response.message || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error(error.message || 'Failed to send broadcast notification');
    } finally {
      setLoading(false);
    }
  };

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case 'pandits':
        return <UserCheck className="h-5 w-5" />;
      case 'users':
        return <Users className="h-5 w-5" />;
      default:
        return <Globe className="h-5 w-5" />;
    }
  };

  const getAudienceCount = () => {
    if (!stats) return 0;
    switch (formData.targetAudience) {
      case 'pandits':
        return stats.totalPanditsWithTokens;
      case 'users':
        return stats.totalRegularUsersWithTokens;
      default:
        return stats.totalUsersWithTokens;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          {/* <div className="p-2 bg-primary/10 rounded-lg">
            <Bell className="h-6 w-6 text-primary" />
          </div> */}
          <h1 className="text-3xl font-bold text-gray-900">Broadcast Notifications</h1>
        </div>
        <p className="text-gray-600">Send announcements to users, pandits, or everyone</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {loadingStats ? (
          <div className="col-span-3 flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : stats ? (
          <>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <Globe className="h-8 w-8 opacity-80" />
                <span className="text-2xl font-bold">{stats.totalUsersWithTokens}</span>
              </div>
              <p className="text-blue-100 text-sm font-medium">Total Users</p>
              <p className="text-blue-200 text-xs mt-1">with active notifications</p>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <UserCheck className="h-8 w-8 opacity-80" />
                <span className="text-2xl font-bold">{stats.totalPanditsWithTokens}</span>
              </div>
              <p className="text-orange-100 text-sm font-medium">Pandits</p>
              <p className="text-orange-200 text-xs mt-1">with active notifications</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-8 w-8 opacity-80" />
                <span className="text-2xl font-bold">{stats.totalRegularUsersWithTokens}</span>
              </div>
              <p className="text-green-100 text-sm font-medium">Regular Users</p>
              <p className="text-green-200 text-xs mt-1">with active notifications</p>
            </div>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Broadcast Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Send Broadcast</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, targetAudience: 'all' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.targetAudience === 'all'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <Globe className="h-6 w-6 mx-auto mb-2" />
                    <span className="block text-sm font-medium">All Users</span>
                    {stats && (
                      <span className="block text-xs text-gray-500 mt-1">
                        {stats.totalUsersWithTokens} users
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, targetAudience: 'pandits' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.targetAudience === 'pandits'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <UserCheck className="h-6 w-6 mx-auto mb-2" />
                    <span className="block text-sm font-medium">Pandits</span>
                    {stats && (
                      <span className="block text-xs text-gray-500 mt-1">
                        {stats.totalPanditsWithTokens} pandits
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, targetAudience: 'users' }))}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.targetAudience === 'users'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <Users className="h-6 w-6 mx-auto mb-2" />
                    <span className="block text-sm font-medium">Users Only</span>
                    {stats && (
                      <span className="block text-xs text-gray-500 mt-1">
                        {stats.totalRegularUsersWithTokens} users
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Notification Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Important Update"
                  maxLength={100}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.title.length}/100 characters
                </p>
              </div>

              {/* Notification Body */}
              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  placeholder="Enter your message here..."
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.body.length}/500 characters
                </p>
              </div>

              {/* Preview */}
              {(formData.title || formData.body) && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    {formData.title && (
                      <p className="font-semibold text-gray-900 mb-1">{formData.title}</p>
                    )}
                    {formData.body && (
                      <p className="text-sm text-gray-600">{formData.body}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !formData.title.trim() || !formData.body.trim()}
                className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Broadcast to {getAudienceCount()} {formData.targetAudience === 'all' ? 'Users' : formData.targetAudience === 'pandits' ? 'Pandits' : 'Users'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Info Panel */}
        <div className="space-y-6">
          {/* Last Send Result */}
          {sendResult && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <h3 className="text-lg font-semibold text-gray-900">Last Broadcast</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-600">Successfully Sent</span>
                  <span className="text-lg font-bold text-green-600">{sendResult.successCount}</span>
                </div>
                
                {sendResult.failureCount > 0 && (
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="text-sm text-gray-600">Failed</span>
                    <span className="text-lg font-bold text-red-600">{sendResult.failureCount}</span>
                  </div>
                )}

                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Target Audience</p>
                  <div className="flex items-center gap-2">
                    {getAudienceIcon(sendResult.targetAudience)}
                    <span className="text-sm font-medium capitalize">{sendResult.targetAudience}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Guidelines */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-5 w-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900">Guidelines</h3>
            </div>
            
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Keep titles concise and clear (max 100 characters)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Write messages in a professional tone</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Only active users with notifications enabled will receive the broadcast</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Use "All Users" for general announcements</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Target specific groups for relevant updates</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Broadcast;
